import React, { useState, useEffect, useCallback } from 'react';
import { getGemeenschappen, addGemeenschap, updateGemeenschap, deleteGemeenschap, getGemeenschappenForUser } from '../services/gemeenschappen';
import { exportGemeenschappenToExcel } from '../services/export';
import {
  deleteGuestGroup,
  getGuestGroups
} from '../services/conviventieGuestGroupsService';
import { getUpcomingConvivenciesReferencingGuestGroup } from '../services/conviventieService';
import { deleteUploadedLeden } from '../services/ledenUploadService';
import { useAuth } from '../context/AuthContext';
import { bisdomOptions, getParochiesForBisdom } from '../config/formOptions';
import EditGemeenschapModal from '../components/EditGemeenschapModal';
import SectionHelpButton from '../components/SectionHelpButton';
import EmptyState from '../components/EmptyState';
import { trackGemeenschapAdded, trackGemeenschapEdited, trackGemeenschapDeleted, trackGuestGroupDeleted, trackExportExecuted } from '../services/analytics';
import './GemeenschappenPage.css';

const GASTENGROEPEN_BISDOM_OPTION = 'Gastengroepen';
const BISDOM_OPTION_SEPARATOR_VALUE = '__separator_gastengroepen__';
const BISDOM_OPTION_SEPARATOR_LABEL = '──────────';

/**
 * Gemeenschappen Page
 * Shows all communities in a table with CRUD operations
 */
const GemeenschappenPage = () => {
  const { user, isAdmin } = useAuth();
  const [gemeenschappen, setGemeenschappen] = useState([]);
  const [filteredGemeenschappen, setFilteredGemeenschappen] = useState([]);
  const [selectedBisdom, setSelectedBisdom] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [availableParochies, setAvailableParochies] = useState([]);

  // Guest groups state (admin-only Gastengroepen source switch)
  const [guestGroups, setGuestGroups] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    bisdom: '',
    parochie: '',
    gemeenschap: '',
    verantwoordelijkeVoornaam: '',
    verantwoordelijkeAchternaam: '',
    verantwoordelijkeEmail: '',
    catechistVoornaam: '',
    catechistAchternaam: '',
    catechistEmail: '',
    aantalLeden: '',
    laatsteEtappe: '',
    laatsteEtappeAfgesloten: false,
    opmerkingen: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const isGuestGroupMode = isAdmin && selectedBisdom === GASTENGROEPEN_BISDOM_OPTION;

  const loadGemeenschappen = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let data;
      if (isAdmin) {
        // Admins see all gemeenschappen
        data = await getGemeenschappen();
      } else {
        // Non-admins only see gemeenschappen where they are verantwoordelijke, catechist, or creator
        data = await getGemeenschappenForUser(user.email);
      }
      setGemeenschappen(data);
    } catch (err) {
      console.error('Error loading gemeenschappen:', err);
      setError('Fout bij het laden van gemeenschappen');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.email]);

  useEffect(() => {
    loadGemeenschappen();
  }, [loadGemeenschappen]);

  // Filter gemeenschappen when selection changes
  useEffect(() => {
    if (selectedBisdom === 'all') {
      setFilteredGemeenschappen(gemeenschappen);
    } else {
      setFilteredGemeenschappen(gemeenschappen.filter(g => g.bisdom === selectedBisdom));
    }
  }, [gemeenschappen, selectedBisdom]);

  const loadGuestGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchedGroups = await getGuestGroups();

      setGuestGroups(fetchedGroups);
    } catch (err) {
      console.error('Error loading gastengroepen:', err);
      setError('Fout bij het laden van gastengroepen');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load guest groups when switching to the admin-only Gastengroepen source
  useEffect(() => {
    if (isGuestGroupMode) {
      loadGuestGroups();
    }
  }, [isGuestGroupMode, loadGuestGroups]);

  const resetForm = () => {
    setFormData({
      bisdom: '',
      parochie: '',
      gemeenschap: '',
      verantwoordelijkeVoornaam: '',
      verantwoordelijkeAchternaam: '',
      verantwoordelijkeEmail: '',
      catechistVoornaam: '',
      catechistAchternaam: '',
      catechistEmail: '',
      aantalLeden: '',
      laatsteEtappe: '',
      laatsteEtappeAfgesloten: false,
      opmerkingen: ''
    });
    setFormErrors({});
    setAvailableParochies([]);
  };

  const openAddModal = () => {
    resetForm();
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setFormData({
      bisdom: item.bisdom || '',
      parochie: item.parochie || '',
      gemeenschap: item.gemeenschap || '',
      verantwoordelijkeVoornaam: item.verantwoordelijkeVoornaam || '',
      verantwoordelijkeAchternaam: item.verantwoordelijkeAchternaam || '',
      verantwoordelijkeEmail: item.verantwoordelijkeEmail || '',
      catechistVoornaam: item.catechistVoornaam || '',
      catechistAchternaam: item.catechistAchternaam || '',
      catechistEmail: item.catechistEmail || '',
      aantalLeden: item.aantalLeden ?? '',
      laatsteEtappe: item.laatsteEtappe || '',
      laatsteEtappeAfgesloten: item.laatsteEtappeAfgesloten || false,
      opmerkingen: item.opmerkingen || ''
    });
    if (item.bisdom) {
      setAvailableParochies(getParochiesForBisdom(item.bisdom));
    }
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'bisdom') {
      const parochies = getParochiesForBisdom(value);
      setAvailableParochies(parochies);
      setFormData(prev => ({
        ...prev,
        bisdom: value,
        parochie: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const errors = {};
    const aantalLedenRaw = String(formData.aantalLeden ?? '').trim();
    const parsedAantalLeden = Number(aantalLedenRaw);

    if (!formData.bisdom.trim()) errors.bisdom = 'Bisdom is verplicht';
    if (!formData.parochie.trim()) errors.parochie = 'Parochie is verplicht';
    if (!formData.gemeenschap.trim()) errors.gemeenschap = 'Gemeenschap is verplicht';
    if (!formData.laatsteEtappe) errors.laatsteEtappe = 'Laatste etappe is verplicht';
    if (aantalLedenRaw !== '' && (!Number.isFinite(parsedAantalLeden) || parsedAantalLeden < 0 || !Number.isInteger(parsedAantalLeden))) {
      errors.aantalLeden = 'Aantal leden moet een positief geheel getal zijn';
    }
    if (formData.verantwoordelijkeEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.verantwoordelijkeEmail)) {
      errors.verantwoordelijkeEmail = 'Ongeldig email adres';
    }
    if (formData.catechistEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.catechistEmail)) {
      errors.catechistEmail = 'Ongeldig email adres';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getFullName = (voornaam, achternaam) => {
    return [voornaam, achternaam]
      .map(part => (part || '').trim())
      .filter(Boolean)
      .join(' ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const aantalLedenRaw = String(formData.aantalLeden ?? '').trim();
      const dataToSave = {
        ...formData,
        aantalLeden: aantalLedenRaw === '' ? null : Number(aantalLedenRaw)
      };

      if (editingItem) {
        await updateGemeenschap(editingItem.id, dataToSave);
        trackGemeenschapEdited({ bisdom: dataToSave.bisdom });
      } else {
        await addGemeenschap(dataToSave);
        trackGemeenschapAdded({ bisdom: dataToSave.bisdom });
      }
      await loadGemeenschappen();
      closeModal();
    } catch (err) {
      console.error('Error saving gemeenschap:', err);
      alert('Fout bij het opslaan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze gemeenschap wilt verwijderen?')) return;
    
    try {
      const itemToDelete = gemeenschappen.find((g) => g.id === id);
      await deleteGemeenschap(id);
      trackGemeenschapDeleted({ bisdom: itemToDelete?.bisdom });
      await loadGemeenschappen();
      closeModal();
    } catch (err) {
      console.error('Error deleting gemeenschap:', err);
      alert('Fout bij het verwijderen');
    }
  };

  const handleExport = () => {
    exportGemeenschappenToExcel(gemeenschappen);
    trackExportExecuted({ export_type: 'gemeenschappen', item_count: gemeenschappen.length });
  };

  const handleDeleteGuestGroup = async (group) => {
    try {
      // Safety check FIRST: does any upcoming conviventie still reference this group?
      const referencing = await getUpcomingConvivenciesReferencingGuestGroup(group.id);

      if (referencing.length > 0) {
        const listing = referencing
          .map((conviventie) => `- ${conviventie.naam || 'Onbekend'} (${conviventie.datum || '-'})`)
          .join('\n');
        const firstWarning =
          `Deze gastengroep wordt nog gebruikt in ${referencing.length} komende conviventie(n):\n\n${listing}\n\n` +
          'Verwijder de gastengroep eerst uit deze conviventies, of ga verder op eigen risico.';
        if (!window.confirm(firstWarning)) return;

        const secondWarning =
          'Laatste waarschuwing: verwijderen kan niet ongedaan gemaakt worden. Wil je deze gastengroep toch verwijderen?';
        if (!window.confirm(secondWarning)) return;

        // Cascade delete uploaded leden for this guest group in all referencing conviventies
        await Promise.all(
          referencing.map((conviventie) =>
            Promise.resolve(deleteUploadedLeden(conviventie.id, group.id, 'guestGroup')).catch((err) => {
              console.warn(`Failed to delete uploaded leden for conviventie ${conviventie.id}:`, err);
            })
          )
        );
      } else if (!window.confirm('Weet je zeker dat je deze gastengroep wilt verwijderen?')) {
        return;
      }

      await deleteGuestGroup(group.id);
      trackGuestGroupDeleted({ had_active_conviventies: referencing.length > 0 });
      await loadGuestGroups();
    } catch (err) {
      console.error('Error deleting gastengroep:', err);
      alert('Fout bij het verwijderen van gastengroep');
    }
  };

  const formatCreatedAt = (value) => {
    if (!value) return '-';
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('nl-NL');
  };

  if (loading) {
    return (
      <div className="gemeenschappen-page">
        <div className="loading">Laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gemeenschappen-page">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="gemeenschappen-page">
      <div className="app-page-header">
        <div className="app-page-heading">
          <div className="app-page-title-row">
            <h2 className="app-page-title">{isGuestGroupMode ? 'Gastengroepen' : 'Gemeenschappen'}</h2>
            <SectionHelpButton
              title="Gemeenschappen"
              purpose="Hier beheert u gemeenschappen met hun verantwoordelijke, etappe en contactgegevens."
              instructions={[
                'Filter op bisdom om de lijst te beperken tot een regio.',
                'Gebruik Nieuwe gemeenschap om een gemeenschap toe te voegen',
                'Gebruik Bewerken om details van een gemeenschap te wijzigen.',
                'Admins kunnen via de optie Gastengroepen onderaan de filterlijst gastengroepen beheren en verwijderen.',
                'Note: alleen gemeenschappen waarvoor u zelf verantwoordelijk bent zijn hier zichtbaar.'
              ]}
            />
          </div>
          <p className="app-page-description">
            {isGuestGroupMode
              ? 'Beheer gastengroepen die bij conviventies uitgenodigd kunnen worden. Gastengroepen zijn alleen zichtbaar voor admins.'
              : 'Beheer gemeenschappen en gebruik filters om snel de juiste records te vinden.'}
          </p>
        </div>
      </div>

      <div className="list-container">
        <div className="list-header">
          <div className="filter-section">
            <label htmlFor="bisdom-filter">Filteren op Bisdom:</label>
            <select
              id="bisdom-filter"
              value={selectedBisdom}
              onChange={(e) => setSelectedBisdom(e.target.value)}
              className="bisdom-select"
            >
              <option value="all">Alle bisdommen</option>
              {bisdomOptions.map(bisdom => (
                <option key={bisdom} value={bisdom}>
                  {bisdom}
                </option>
              ))}
              {isAdmin && (
                <>
                  <option value={BISDOM_OPTION_SEPARATOR_VALUE} disabled>
                    {BISDOM_OPTION_SEPARATOR_LABEL}
                  </option>
                  <option value={GASTENGROEPEN_BISDOM_OPTION}>Gastengroepen</option>
                </>
              )}
            </select>
          </div>
          {!isGuestGroupMode && (
            <div className="header-actions">
              {isAdmin && (
                <button className="export-button" onClick={handleExport}>
                  Exporteren
                </button>
              )}
              <button className="add-button" onClick={openAddModal}>
                + Nieuwe gemeenschap
              </button>
            </div>
          )}
        </div>

        {isGuestGroupMode ? (
          guestGroups.length === 0 ? (
            <EmptyState
              icon="👥️"
              message="Nog geen gastengroepen. Gastengroepen worden aangemaakt bij het samenstellen van een conviventie."
            />
          ) : (
            <div className="table-wrapper">
              <table className="gemeenschappen-table">
                <thead>
                  <tr>
                    <th>Naam</th>
                    <th>Aangemaakt door</th>
                    <th>Aangemaakt op</th>
                    <th>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {guestGroups.map((group) => (
                    <tr key={group.id}>
                      <td data-label="Naam" className="cell-primary">{group.name || '-'}</td>
                      <td data-label="Aangemaakt door">{group.createdBy || '-'}</td>
                      <td data-label="Aangemaakt op">{formatCreatedAt(group.createdAt)}</td>
                      <td className="actions-cell">
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteGuestGroup(group)}
                          title="Verwijderen"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : filteredGemeenschappen.length === 0 ? (
          <EmptyState
            icon="🏠️"
            message={selectedBisdom === 'all' ? 'Nog geen gemeenschappen toegevoegd' : 'Geen gemeenschappen in dit bisdom'}
            actionLabel="Nieuwe gemeenschap"
            onAction={openAddModal}
          />
        ) : (
          <div className="table-wrapper">
            <table className="gemeenschappen-table">
            <thead>
              <tr>
                <th>Gemeenschap</th>
                {isAdmin && <th>Verantwoordelijke</th>}
                {isAdmin && <th>Catechist</th>}
                <th>Etappe</th>
                {isAdmin && <th>Broeders</th>}
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {filteredGemeenschappen.map(item => (
                <tr key={item.id}>
                  <td data-label="Gemeenschap" className="stacked-cell">
                    <div className="cell-badge">{item.bisdom || '-'}</div>
                    <div className="cell-primary">{item.parochie || '-'}</div>
                    <div className="cell-primary">{item.gemeenschap || '-'}</div>
                  </td>
                  {isAdmin && (
                    <td data-label="Verantwoordelijke" className="stacked-cell">
                      <div className="cell-primary">{getFullName(item.verantwoordelijkeVoornaam, item.verantwoordelijkeAchternaam) || '-'}</div>
                      <div className="cell-secondary email-link">
                        {item.verantwoordelijkeEmail ? <a href={`mailto:${item.verantwoordelijkeEmail}`}>{item.verantwoordelijkeEmail}</a> : '-'}
                      </div>
                    </td>
                  )}
                  {isAdmin && (
                    <td data-label="Catechist" className="stacked-cell">
                      <div className="cell-primary">{getFullName(item.catechistVoornaam, item.catechistAchternaam) || '-'}</div>
                      <div className="cell-secondary email-link">
                        {item.catechistEmail ? <a href={`mailto:${item.catechistEmail}`}>{item.catechistEmail}</a> : '-'}
                      </div>
                    </td>
                  )}
                  <td data-label="Etappe" className="stacked-cell">
                    <div className="cell-primary">{item.laatsteEtappe || '-'}</div>
                    <div className="cell-secondary status-inline">
                      {item.laatsteEtappeAfgesloten ? (
                        <span className="status-badge completed">✓ Afgesloten</span>
                      ) : (
                        <span className="status-badge pending">- Niet afgesloten</span>
                      )}
                    </div>
                  </td>
                  {isAdmin && <td data-label="Broeders">{item.aantalLeden ?? '-'}</td>}
                  <td className="actions-cell">
                    {(isAdmin || item.verantwoordelijkeEmail === user?.email || item.catechistEmail === user?.email || item.createdBy === user?.email) && (
                      <button 
                        className="action-btn edit"
                        onClick={() => openEditModal(item)}
                        title="Bewerken"
                      >
                        ✎
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <EditGemeenschapModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        isEditMode={editingItem !== null}
        formData={formData}
        onChange={handleChange}
        formErrors={formErrors}
        availableParochies={availableParochies}
        onDelete={() => handleDelete(editingItem?.id)}
      />
    </div>
  );
};

export default GemeenschappenPage;
